import React, { useState, useMemo } from 'react';
import { EPaper } from '../types/epaper';
import EPaperCard from './EPaperCard';
import { Filter, ChevronDown, ChevronUp } from 'lucide-react';
import './SpecialEditions.css';

interface SpecialEditionsProps {
  specialEditions: EPaper[];
  onSelectEPaper: (epaper: EPaper) => void;
}

// Helper function to get badge display text and styling
const getBadgeInfo = (category: string | null | undefined) => {
  if (!category) return null;
  
  const badgeConfig = {
    handbook: { text: 'Handbook', className: 'badge-handbook' },
    review: { text: 'Review', className: 'badge-review' },
    outlook: { text: 'Outlook', className: 'badge-outlook' },
    special: { text: 'Special', className: 'badge-special' },
    annual: { text: 'Annual', className: 'badge-annual' },
    quarterly: { text: 'Quarterly', className: 'badge-quarterly' }
  };
  
  return badgeConfig[category as keyof typeof badgeConfig] || null;
};

const SpecialEditions: React.FC<SpecialEditionsProps> = ({ specialEditions, onSelectEPaper }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isExpanded, setIsExpanded] = useState<boolean>(true);

  // Get unique categories for filter
  const categories = useMemo(() => {
    const categorySet = new Set(
      specialEditions
        .map(edition => edition.category)
        .filter(category => category !== null && category !== undefined)
    );
    return Array.from(categorySet).sort();
  }, [specialEditions]);

  // Filter special editions by selected category
  const filteredEditions = useMemo(() => {
    if (selectedCategory === 'all') {
      return specialEditions;
    }
    return specialEditions.filter(edition => edition.category === selectedCategory);
  }, [specialEditions, selectedCategory]);

  if (specialEditions.length === 0) {
    return null;
  }

  return (
    <div className="special-editions-container">
      <div className="special-editions-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="header-content">
          <h2>Special Editions ({specialEditions.length})</h2>
          <p>Exclusive handbooks, reviews, and special publications</p>
        </div>
        <button className="expand-toggle">
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>
      
      {isExpanded && (
        <>
          {/* Category Filter */}
          {categories.length > 1 && (
            <div className="special-editions-filter">
              <div className="filter-container">
                <Filter size={16} />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="category-filter"
                >
                  <option value="all">All Categories</option>
                  {categories.map(category => {
                    const badgeInfo = getBadgeInfo(category);
                    return (
                      <option key={category} value={category || ''}>
                        {badgeInfo?.text || category}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="filter-stats">
                <span>{filteredEditions.length} of {specialEditions.length} Special Editions</span>
              </div>
            </div>
          )}
          
          <div className="special-editions-grid">
            {filteredEditions.map((edition) => {
              const badgeInfo = getBadgeInfo(edition.category);
              
              return (
                <div key={edition.id} className="special-edition-card">
                  <EPaperCard
                    epaper={edition}
                    onClick={() => onSelectEPaper(edition)}
                    viewMode="grid"
                  />
                  {badgeInfo && (
                    <div className={`special-edition-badge ${badgeInfo.className}`}>
                      {badgeInfo.text}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default SpecialEditions; 